import styled from 'styled-components';
import { fadeIn } from './animations';
import { theme } from '../../styles/theme';

/**
 * Componentes styled-components GENÉRICOS e REUTILIZÁVEIS
 * Extraídos de LandingPage.tsx para facilitar manutenção e reuso
 */

// ============================================
// CONTAINERS
// ============================================

/**
 * Container base - wrapper padrão com max-width e padding responsivo
 */
export const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

/**
 * Container com animação de entrada (fade + blur + scale)
 * Usa prop $isVisible para controlar visibilidade
 */
export const AnimatedContainer = styled(Container)<{ $isVisible?: boolean }>`
  opacity: ${props => props.$isVisible ? 1 : 0};
  filter: blur(${props => props.$isVisible ? '0px' : '8px'});
  transform: scale(${props => props.$isVisible ? 1 : 0.95});
  transition: opacity 1.2s cubic-bezier(0.16, 1, 0.3, 1),
              filter 1.2s cubic-bezier(0.16, 1, 0.3, 1),
              transform 1.2s cubic-bezier(0.16, 1, 0.3, 1);
  will-change: opacity, filter, transform;
`;

// ============================================
// BUTTONS
// ============================================

/**
 * Botão primário - CTA principal (gradiente vermelho Loopra)
 */
export const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #ff4444, #ff2222);
  color: white;
  border: none;
  border-radius: ${theme.borders.radius.full};
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 8px 32px rgba(255, 68, 68, 0.4);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 16px 24px;
  }
`;

/**
 * Botão secundário - ação alternativa (transparente com borda)
 */
export const SecondaryButton = styled.button`
  background: transparent;
  color: #a1a1aa;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: ${theme.borders.radius.full};
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: #fafafa;
    border-color: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 14px;

    img {
      width: 24px;
      height: 24px;
    }

    div {
      font-size: 13px;
    }
  }
`;

// ============================================
// SECTION HEADERS
// ============================================

/**
 * Header de seção - wrapper para título + subtítulo
 */
export const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
  animation: ${fadeIn} 0.6s ease-out;
  padding: 0 16px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
    padding: 0 12px;
  }
`;

/**
 * Título de seção - gradiente de texto com hover effect
 */
export const EnhancedSectionTitle = styled.h2`
  font-size: clamp(32px, 5vw, 48px);
  font-weight: 700;
  margin: 0 0 16px;
  background: linear-gradient(180deg, #fafafa 0%, #a1a1aa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  display: inline-block;
  width: 100%;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  will-change: transform;

  &:hover {
    transform: translateY(-2px);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 3px;
    background: linear-gradient(90deg, #ff4444, #ff6666);
    transition: width 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
    box-shadow: 0 0 12px rgba(255, 68, 68, 0.6);
  }

  &:hover::after {
    width: 120px;
  }
`;

/**
 * Subtítulo de seção - texto secundário descritivo
 */
export const SectionSubtitle = styled.p`
  font-size: clamp(16px, 2vw, 20px);
  color: #71717a;
  margin: 0 auto;
  max-width: 600px;
`;
